import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';

const HighlightsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container maxWidth="lg">
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 6, md: 8 },
        alignItems: 'center',
        py: { 
          xs: 4, 
          sm: 6, 
          md: 8, 
          lg: 10
        }
      }}>
        {/* Image Section */}
        <Box sx={{ 
          flex: 1,
          maxWidth: { md: '50%' },
          width: '100%',
          position: 'relative'
        }}>
          <Box
            component="img"
            src="/highlights.png"
            alt="Caja de verduras ecológicas"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: 2,
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Box>
        
        {/* Content Section */}
        <Box sx={{ 
          flex: 1,
          maxWidth: { md: '50%' },
          width: '100%',
          position: 'relative',
          zIndex: 2
        }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              color: '#000000',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
              fontWeight: 300,
              lineHeight: { xs: 1.3, sm: 1.25, md: 1.2 },
              fontSize: {
                xs: '1.25rem',
                sm: '1.75rem',
                md: '2.8rem',
                lg: '3.2rem',
                xl: '3.5rem'
              },
              // Ultra-wide screens
              '@media (min-width: 1920px)': {
                fontSize: '4rem'
              },
              // Small mobile
              '@media (max-width: 480px)': {
                fontSize: '1.2rem',
                lineHeight: 1.35
              },
              letterSpacing: { xs: '0.5px', sm: '1px' },
              fontFamily: '"Lora", serif',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'manual'
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Alimentos ecológicos</span><br />
            <span style={{ whiteSpace: 'nowrap' }}>y formación práctica</span>
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: '#000000',
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: {
                xs: '0.95rem',
                sm: '1rem',
                md: '1.1rem',
                lg: '1.15rem',
                xl: '1.2rem'
              },
              // Ultra-wide screens
              '@media (min-width: 1920px)': {
                fontSize: '1.3rem'
              },
              // Small mobile
              '@media (max-width: 480px)': {
                fontSize: '0.9rem'
              },
              lineHeight: 1.6
            }}
          >
            Comida ecológica fresca y accesible, directa de nuestra finca a tu mesa.
          </Typography>
          
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr'
              },
              columnGap: {
                xs: 0,
                sm: 2,
                md: 3,
                lg: 4
              },
              rowGap: {
                xs: 2,
                sm: 2.5,
                md: 3
              },
              mt: { xs: 1, sm: 1.5, md: 2 }
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 14C16 16.209 14.209 18 12 18C9.791 18 8 16.209 8 14C8 11.791 9.791 10 12 10C14.209 10 16 11.791 16 14Z" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                    <path d="M12 6V2M6.34315 7.34315L3.51472 4.51472M17.6569 7.34315L20.4853 4.51472M4 14H2M22 14H20M6.34315 20.6569L3.51472 23.4853M17.6569 20.6569L20.4853 23.4853" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                  </svg>
                </Box>
                <Typography variant="h6" fontWeight="600" sx={{ 
                  fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem', lg: '1.4rem' },
                  color: '#455b40',
                  lineHeight: 1.2,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  flexShrink: 0
                }}>
                  Certificación ecológica
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ 
                color: '#14250c', 
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                lineHeight: 1.5,
                pl: { xs: 0, sm: 7 }
              }}>
                Alimentos orgánicos certificados desde 2000, cultivados con respeto ambiental.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 14C16 16.209 14.209 18 12 18C9.791 18 8 16.209 8 14C8 11.791 9.791 10 12 10C14.209 10 16 11.791 16 14Z" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                    <path d="M12 6V2M6.34315 7.34315L3.51472 4.51472M17.6569 7.34315L20.4853 4.51472M4 14H2M22 14H20M6.34315 20.6569L3.51472 23.4853M17.6569 20.6569L20.4853 23.4853" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                  </svg>
                </Box>
                <Typography variant="h6" fontWeight="600" sx={{ 
                  fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem', lg: '1.4rem' },
                  color: '#455b40',
                  lineHeight: 1.2,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  flexShrink: 0
                }}>
                  Formación práctica
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ 
                color: '#14250c', 
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                lineHeight: 1.5,
                pl: { xs: 0, sm: 7 }
              }}>
                Talleres para agricultores y personas interesadas en cultivo ecológico.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 14C16 16.209 14.209 18 12 18C9.791 18 8 16.209 8 14C8 11.791 9.791 10 12 10C14.209 10 16 11.791 16 14Z" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                    <path d="M12 6V2M6.34315 7.34315L3.51472 4.51472M17.6569 7.34315L20.4853 4.51472M4 14H2M22 14H20M6.34315 20.6569L3.51472 23.4853M17.6569 20.6569L20.4853 23.4853" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                  </svg>
                </Box>
                <Typography variant="h6" fontWeight="600" sx={{ 
                  fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem', lg: '1.4rem' },
                  color: '#455b40',
                  lineHeight: 1.2,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  flexShrink: 0
                }}>
                  Comunidad
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ 
                color: '#14250c', 
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                lineHeight: 1.5,
                pl: { xs: 0, sm: 7 }
              }}>
                Redes de apoyo entre agricultores, familias y consumidores conscientes.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 14C16 16.209 14.209 18 12 18C9.791 18 8 16.209 8 14C8 11.791 9.791 10 12 10C14.209 10 16 11.791 16 14Z" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                    <path d="M12 6V2M6.34315 7.34315L3.51472 4.51472M17.6569 7.34315L20.4853 4.51472M4 14H2M22 14H20M6.34315 20.6569L3.51472 23.4853M17.6569 20.6569L20.4853 23.4853" 
                          stroke="#000000" 
                          strokeWidth="0.5" 
                          fill="none"/>
                  </svg>
                </Box>
                <Typography variant="h6" fontWeight="600" sx={{ 
                  fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem', lg: '1.4rem' },
                  color: '#455b40',
                  lineHeight: 1.2,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  flexShrink: 0
                }}>
                  Venta directa
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ 
                color: '#14250c', 
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                lineHeight: 1.5,
                pl: { xs: 0, sm: 7 }
              }}>
                Granadas y hortalizas frescas de temporada en Valencia y alrededores.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default HighlightsSection;